/* Buy block behaviour.

   Variant selection, the quantity stepper, the gift hint and the dispatch
   line. The dispatch line counts down to a real cutoff and switches to the
   next working day once it passes: no timer that resets on reload, which
   would be fabricated urgency. */

(() => {
  const form = document.getElementById('kz-buy-form');
  if (!form) return;

  const root = form.closest('.kz-buy');
  const variantField = form.querySelector('[data-kz-variant-id]');
  const priceEl = root.querySelector('[data-kz-price]');
  const compareEl = root.querySelector('[data-kz-compare]');
  const colourName = root.querySelector('[data-kz-colour-name]');

  let variants = [];
  try {
    variants = JSON.parse(document.getElementById('kz-variants')?.textContent || '[]');
  } catch (e) {
    variants = [];
  }

  /* The set is a bundle product of its own, so choosing it has to switch the
     form to that product's variant, not just tick a radio. It carries one
     option per component, so the partner's colour has to be asked for too. */
  const setNode = document.getElementById('kz-set-variants');
  let setVariants = [];
  try {
    setVariants = JSON.parse(setNode?.textContent || '[]');
  } catch (e) {
    setVariants = [];
  }
  const setOwnIndex = parseInt(setNode?.dataset.ownIndex ?? '0', 10);
  const partnerBox = root.querySelector('[data-kz-partner]');
  const saveStrip = root.querySelector('[data-kz-savestrip]');
  const partnerName = root.querySelector('[data-kz-partner-name]');

  const money = (cents) => {
    const fmt = document.getElementById('kz-variants')?.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    const amount = (cents / 100).toFixed(2);
    return fmt
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount}}', amount)
      .replace(/\{\{[^}]+\}\}/g, amount);
  };

  // --- Variant selection ---------------------------------------------------

  const selectedOptions = () =>
    [...form.querySelectorAll('input[name="kz-colour"]:checked')].map((i) => i.value);

  const isBundle = () =>
    setVariants.length > 0 &&
    form.querySelector('input[name="kz-set"]:checked')?.value === 'bundle';

  const partnerValue = () =>
    form.querySelector('input[name="kz-partner"]:checked')?.value || null;

  /* The button shows what the tap will cost: unit price times quantity. The
     unit price is set by the variant sync, the quantity by the stepper, and
     either one calls this. */
  let unitPrice = 0;
  const renderButtonPrice = () => {
    const btnPrice = form.querySelector('[data-kz-add-price]');
    if (!btnPrice || !unitPrice) return;
    const q = Math.max(1, parseInt(form.querySelector('[data-kz-qty-input]')?.value || '1', 10) || 1);
    btnPrice.textContent = money(unitPrice * q);
  };

  const syncVariant = () => {
    const chosen = selectedOptions();
    if (!chosen.length || !variants.length) return;

    const bundle = isBundle();
    if (partnerBox) partnerBox.hidden = !bundle;
    if (saveStrip) saveStrip.hidden = !bundle;

    let match;
    if (bundle) {
      const partner = partnerValue();
      const wanted = [];
      wanted[setOwnIndex] = chosen[0];
      wanted[setOwnIndex === 0 ? 1 : 0] = partner;
      match = setVariants.find((v) => v.options.join('/') === wanted.join('/'));
      if (partnerName && partner) partnerName.textContent = ' : ' + partner;
    } else {
      match = variants.find((v) => v.options.join('/') === chosen.join('/'));
    }
    if (!match) return;

    variantField.value = match.id;
    if (priceEl) priceEl.textContent = money(match.price);

    const strike = (el, v) => {
      if (!el) return;
      const on = !!(v.compare_at_price && v.compare_at_price > v.price);
      if (on) el.textContent = money(v.compare_at_price);
      el.hidden = !on;
    };
    strike(compareEl, match);
    /* The set has no compare-at of its own in Shopify Bundles; the card
       already carries the two components' reference added up, so the price
       line shows that same figure while the set is chosen. */
    if (bundle && compareEl && compareEl.hidden) {
      const setWas = root.querySelector('[data-kz-set-was]');
      if (setWas && !setWas.hidden && setWas.textContent.trim()) {
        compareEl.textContent = setWas.textContent.trim();
        compareEl.hidden = false;
      }
    }

    /* The two option cards keep their own figures whichever one is ticked:
       the solo card follows the solo variant in the chosen colour, the set
       card follows the set variant for that colour pair. */
    const soloMatch = variants.find((v) => v.options.join('/') === chosen.join('/'));
    if (soloMatch) {
      const sp = root.querySelector('[data-kz-solo-price]');
      if (sp) sp.textContent = money(soloMatch.price);
      strike(root.querySelector('[data-kz-solo-was]'), soloMatch);
    }
    let setMatch = null;
    if (setVariants.length && setOwnIndex >= 0) {
      const w = [];
      w[setOwnIndex] = chosen[0];
      w[setOwnIndex === 0 ? 1 : 0] = partnerValue();
      setMatch = setVariants.find((v) => v.options.join('/') === w.join('/')) || null;
      if (setMatch) {
        const sn = root.querySelector('[data-kz-set-now]');
        if (sn) sn.textContent = money(setMatch.price);
        if (setMatch.compare_at_price && setMatch.compare_at_price > setMatch.price) {
          strike(root.querySelector('[data-kz-set-was]'), setMatch);
        }
      }
    }

    if (colourName) colourName.textContent = ' : ' + chosen[0];

    /* The main slide follows the colour, but only after the shopper has
       picked one: the page opens on the editor's main image. A variant
       with its own picture then replaces it and its thumbnail; one without
       leaves the gallery as it was. */
    /* With the set chosen, the set's own picture (both products together)
       takes the slide; the solo picture comes back when the set is dropped. */
    const pic = bundle && setMatch && setMatch.image ? setMatch : soloMatch;
    if (galleryFollows && pic && pic.image) {
      root.dispatchEvent(new CustomEvent('kz:gallery-restore'));
      const mainImg = root.querySelector('.kz-gal-slide--main img');
      if (mainImg && mainImg.src !== pic.image) {
        mainImg.src = pic.image;
        if (pic.srcset) mainImg.srcset = pic.srcset;
      }
      const thumbImg = root.querySelector('[data-kz-thumb="0"] img');
      if (thumbImg && pic.thumb) { thumbImg.src = pic.thumb; thumbImg.removeAttribute('srcset'); }
      // On the phone the track may sit on another slide: bring the main
      // slide back so the new colour is what the shopper actually sees.
      root.dispatchEvent(new CustomEvent('kz:gallery-home'));
    }

    form.action = '/cart/add';

    unitPrice = match.price;
    const cta = form.querySelector('[data-kz-add]');
    if (cta) {
      cta.disabled = !match.available;
      const label = cta.querySelector('[data-kz-add-label]');
      if (label) label.textContent = match.available ? cta.dataset.labelAvailable : cta.dataset.labelSoldOut;
      cta.querySelector('.kz-cta-sep')?.toggleAttribute('hidden', !match.available);
      cta.querySelector('[data-kz-add-price]')?.toggleAttribute('hidden', !match.available);
    }
    renderButtonPrice();

    // Keep the URL shareable without reloading the page.
    const url = new URL(window.location);
    url.searchParams.set('variant', match.id);
    window.history.replaceState({}, '', url);
  };

  let galleryFollows = false;
  form
    .querySelectorAll('input[name="kz-colour"], input[name="kz-set"], input[name="kz-partner"]')
    .forEach((i) => i.addEventListener('change', () => { galleryFollows = true; syncVariant(); }));

  /* ?set=bundle preselects the set. It is how every "prendre le set" link on
     the site arrives here, and it makes the set a shareable, advertisable URL
     of its own now that the bundle product has no page of its own. */
  const chooseSet = (which) => {
    const radio = form.querySelector(`input[name="kz-set"][value="${which}"]`);
    if (!radio || radio.checked) return false;
    radio.checked = true;
    syncVariant();
    return true;
  };

  const wanted = new URLSearchParams(window.location.search).get('set');
  if (wanted === 'bundle' || wanted === 'solo') chooseSet(wanted);

  /* Those same links can be clicked while already on this page, where a full
     reload to change one radio would be absurd. Take them over and scroll. */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="set=bundle"], a[href*="set=solo"]');
    if (!a) return;
    let target;
    try { target = new URL(a.href, window.location.origin); } catch (err) { return; }
    if (target.pathname !== window.location.pathname) return;
    e.preventDefault();
    chooseSet(target.searchParams.get('set'));
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  syncVariant();

  // --- Quantity ------------------------------------------------------------

  const qtyInput = form.querySelector('[data-kz-qty-input]');
  const gift = root.querySelector('[data-kz-gift]');
  const max = parseInt(qtyInput?.max || '5', 10);

  const syncQty = () => {
    let v = parseInt(qtyInput.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    if (v > max) v = max;
    qtyInput.value = v;
    form.querySelector('[data-kz-qty="-1"]').disabled = v <= 1;
    form.querySelector('[data-kz-qty="1"]').disabled = v >= max;
    if (gift) gift.hidden = v <= 1;
    renderButtonPrice();
  };

  form.querySelectorAll('[data-kz-qty]').forEach((b) =>
    b.addEventListener('click', () => {
      qtyInput.value = parseInt(qtyInput.value, 10) + parseInt(b.dataset.kzQty, 10);
      syncQty();
    })
  );
  qtyInput?.addEventListener('change', syncQty);
  if (qtyInput) syncQty();

  // --- Title on one line ---------------------------------------------------

  /* Shrinks the title, down to a floor, until it fits one line. A title too
     long for the floor wraps as normal; nothing is ever clipped. */
  const h1 = root.querySelector('.kz-buy-h1');
  const fitTitle = () => {
    if (!h1) return;
    h1.style.fontSize = '';
    h1.style.whiteSpace = 'nowrap';
    let size = parseFloat(getComputedStyle(h1).fontSize);
    const floor = 20;
    while (h1.scrollWidth > h1.clientWidth && size > floor) {
      size -= 1;
      h1.style.fontSize = size + 'px';
    }
    if (h1.scrollWidth > h1.clientWidth) {
      h1.style.whiteSpace = '';
      h1.style.fontSize = floor + 'px';
    }
  };
  fitTitle();
  window.addEventListener('resize', fitTitle);
  if (document.fonts?.ready) document.fonts.ready.then(fitTitle);

  // --- Gallery -------------------------------------------------------------

  /* Video tiles carry their file in data-src and fetch nothing until they
     come near the viewport. On phones the video tile sits one swipe away;
     on desktop it is in the grid, so it starts as soon as the page settles. */
  const lazyVideos = [...root.querySelectorAll('video[data-src]')];
  const startVideo = (v) => {
    if (!v.dataset.src) return;
    const s = document.createElement('source');
    s.src = v.dataset.src;
    if (v.dataset.type) s.type = v.dataset.type;
    v.appendChild(s);
    v.removeAttribute('data-src');
    v.load();
    v.play().catch(() => {});
  };
  /* On phones the track scrolls sideways, so a slide one swipe away still
     counts as "in the viewport" for an observer. There the video waits for
     its slide to become the current one (see mark() below). On desktop the
     grid is static and the observer starts it once it is near. */
  const wideMQ = window.matchMedia('(min-width: 760px)');
  if (lazyVideos.length && wideMQ.matches) {
    if ('IntersectionObserver' in window) {
      const vio = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { startVideo(e.target); vio.unobserve(e.target); } });
      }, { rootMargin: '200px 0px' });
      lazyVideos.forEach((v) => vio.observe(v));
    } else {
      lazyVideos.forEach(startVideo);
    }
  }

  /* Desktop: a tile is a button. Clicking it trades pictures with the main
     slide, so the chosen picture opens large and the one it replaces takes
     the tile's place. Nothing leaves the gallery. Phones keep the swipe
     track, where the tile already is the picture being looked at. */
  const mainSlide = root.querySelector('.kz-gal-slide--main');
  const isWide = window.matchMedia('(min-width: 760px)');
  /* A tile's picture is its <img>, or its <video> on a video tile. */
  const picOf = (el) => el.querySelector(':scope > img, :scope > video');
  const openingImg = mainSlide ? picOf(mainSlide) : null;
  /* Trade the pictures of the main slide and one tile. Called again with the
     same tile it undoes itself, which is how a colour change first puts the
     opening picture back before the variant's picture replaces it. */
  const trade = (slide) => {
    const big = picOf(mainSlide);
    const small = picOf(slide);
    if (!big || !small) return;
    const mark = document.createComment('swap');
    mainSlide.replaceChild(mark, big);
    slide.replaceChild(big, small);
    mainSlide.replaceChild(small, mark);
    /* Moving a <video> in the DOM pauses it in some browsers. Its play
       badge moves with it. */
    [big, small].forEach((el) => {
      if (el.tagName !== 'VIDEO') return;
      startVideo(el);
      el.play().catch(() => {});
      const from = el === big ? mainSlide : slide;
      const badge = from.querySelector(':scope > .kz-gal-play');
      if (badge) el.parentElement.appendChild(badge);
    });
    /* A tile is square; while one sits in the main slot, the slot is square
       too, so the picture fits whole. The opening picture brings the wide
       slot back. */
    mainSlide.classList.toggle('kz-gal-slide--square', picOf(mainSlide) !== openingImg);
  };
  const restore = () => {
    if (!mainSlide || !openingImg || openingImg.parentElement === mainSlide) return;
    trade(openingImg.parentElement);
  };
  root.addEventListener('kz:gallery-restore', restore);
  if (mainSlide) {
    root.querySelectorAll('.kz-gal-slide:not(.kz-gal-slide--main)').forEach((slide) => {
      if (!picOf(slide)) return;
      slide.setAttribute('role', 'button');
      slide.tabIndex = 0;
      const swap = () => {
        if (!isWide.matches) return;
        if (picOf(slide) === openingImg) { trade(slide); }
        else { restore(); trade(slide); }
        mainSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      };
      slide.addEventListener('click', swap);
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); swap(); }
      });
    });
  }

  /* Phone only in effect: from 760px the track is a grid and never scrolls,
     so the thumb strip is hidden and none of this fires. */
  const track = root.querySelector('[data-kz-track]');
  const thumbs = [...root.querySelectorAll('[data-kz-thumb]')];
  if (track && thumbs.length) {
    const slides = [...track.querySelectorAll('[data-kz-slide]')];

    const mark = (i) => {
      /* The slide in view is "current": a video there drops its play
         badge, since the thing it announces is already on screen. */
      slides.forEach((s, k) => {
        s.classList.toggle('is-current', k === i);
        if (k === i) { const v = s.querySelector(':scope > video[data-src]'); if (v) startVideo(v); }
      });
      thumbs.forEach((t, k) => {
        const on = k === i;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      // Keep the highlighted thumb in view inside its own strip.
      thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    };

    root.addEventListener('kz:gallery-home', () => {
      if (track.scrollLeft === 0) return;
      track.scrollTo({ left: 0, behavior: 'smooth' });
      mark(0);
    });

    thumbs.forEach((t) =>
      t.addEventListener('click', () => {
        const i = parseInt(t.dataset.kzThumb, 10);
        if (!slides[i]) return;
        // Every slide is exactly one track width, so the index is the offset.
        track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
        mark(i);
      })
    );

    // Arrows step one slide; disabled at either end.
    const arrows = [...root.querySelectorAll('[data-kz-arrow]')];
    const current = () => Math.round(track.scrollLeft / (track.clientWidth || 1));
    const syncArrows = () => {
      const i = current();
      arrows.forEach((a) => {
        const d = parseInt(a.dataset.kzArrow, 10);
        a.disabled = d < 0 ? i <= 0 : i >= slides.length - 1;
      });
    };
    arrows.forEach((a) =>
      a.addEventListener('click', () => {
        const next = Math.min(slides.length - 1, Math.max(0, current() + parseInt(a.dataset.kzArrow, 10)));
        track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
        mark(next);
        // Reflect the target at once rather than after the scroll settles.
        arrows.forEach((b) => {
          const d = parseInt(b.dataset.kzArrow, 10);
          b.disabled = d < 0 ? next <= 0 : next >= slides.length - 1;
        });
      })
    );
    syncArrows();
    slides[0]?.classList.add('is-current');

    let raf = 0;
    track.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const w = track.clientWidth || 1;
        const i = Math.round(track.scrollLeft / w);
        if (i >= 0 && i < thumbs.length && !thumbs[i].classList.contains('is-active')) mark(i);
        syncArrows();
      });
    }, { passive: true });
  }

  // --- Dispatch line -------------------------------------------------------

  const dispatch = root.querySelector('[data-kz-dispatch]');
  if (dispatch) {
    const out = dispatch.querySelector('[data-kz-dispatch-text]');
    const cutoff = parseInt(dispatch.dataset.cutoff, 10);
    const lang = document.documentElement.lang || 'fr';

    const render = () => {
      const now = new Date();
      const target = new Date(now);
      target.setHours(cutoff, 0, 0, 0);
      // Past today's cutoff the next one is tomorrow, and nothing leaves
      // over a weekend, so roll on to Monday rather than promise a Sunday.
      if (now >= target) target.setDate(target.getDate() + 1);
      while (target.getDay() === 0 || target.getDay() === 6) {
        target.setDate(target.getDate() + 1);
      }

      const ms = target - now;
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const left = h + 'h ' + (m < 10 ? '0' : '') + m + 'm';

      const days = Math.round(
        (new Date(target).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / 86400000
      );
      let tpl;
      if (days === 0) tpl = dispatch.dataset.today;
      else if (days === 1) tpl = dispatch.dataset.tomorrow;
      else tpl = (dispatch.dataset.day || '').replace(
        '[j]', target.toLocaleDateString(lang, { weekday: 'long' })
      );

      out.textContent = (tpl || '').replace('[t]', left);
    };

    render();
    setInterval(render, 60000);
  }
})();
